import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useContent } from '@/stores/RootStore';
import { useReveal } from '@/hooks/useReveal';
import Button from '@/components/ui/Button';
import { INSTRUMENTS, SAFETY_TERMINALS, PRODUCT_DETAIL_ROUTE } from '@/data/mockData';
import type { Instrument } from '@/types';
import s from './Instruments.module.scss';

// IoT 계측기 · 단말기 — D안 '① 주력 제품·계측기'의 제품 축을 두 그룹으로 노출.
//   [2026-09 사용자 확정] 데이터 사이트(evstech 제품소개) 실측 결과: 계측기 4종(전류·차압·온도·pH)=환경(방지시설).
//   → '환경 계측기' + '안전 단말기(체감온도계 · 가스검침 예정)' 두 그룹으로 두 도메인 균형(D안 ① 완전 반영).
//   가스검침기는 아직 제품 없음 → soon:true '예정' 플레이스홀더로 자리 예약(사용자 지시).
//   [2026-09-09 사용자] 카드 클릭 → /products 해당 유형 필터로 이동(계측기=?type=instrument, 안전단말기=?type=terminal).
//     예정(soon) 카드는 제품이 없어 비클릭. react-router navigate 라 뒤로가기 시 이 섹션으로 복원. [[evs-back-restores-scroll]]
const cardInner = (it: Instrument) => (
  <>
    <div className={`${s.media} ${it.fill ? s.fill : ''}`}>
      {it.soon && <span className={s.badge}>준비중</span>}
      {it.image ? (
        <img src={it.image} alt={it.label} loading="lazy" />
      ) : it.soon ? (
        // 가스검침 예정: 가스/분자 라인 아이콘(뮤트)
        <svg className={s.soonIcon} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="17" cy="30" r="6" /><circle cx="31" cy="30" r="6" /><circle cx="24" cy="18" r="6" />
          <path d="M20.5 22.5l-1 3.5M27.5 22.5l1 3.5M21 30h6" />
        </svg>
      ) : (
        <div className="placeholder-box" />
      )}
    </div>
    <div className={s.body}>
      <h3 className={s.label}>{it.label}</h3>
      {it.desc && <p className={s.desc}>{it.desc}</p>}
    </div>
  </>
);

const Instruments = observer(() => {
  const { instrumentsSection } = useContent();
  const reveal = useReveal<HTMLDivElement>();
  const navigate = useNavigate();
  const go = (to?: string) => (e: React.MouseEvent) => { if (!to) return; e.preventDefault(); navigate(to); };

  // 카드: 클릭 가능(제품 있음)이면 <a>, 예정이면 <article>.
  //   [2026-09-09 사용자] /products 의 '해당 제품 상세 섹션'으로 앵커.
  //     계측기(i1~i4) → #p-i1~#p-i4 (그 제품의 큰 상세 블록, #instruments 섹션).
  //     안전 단말기 체감온도계(t1) → #t1 (카탈로그 카드. 별도 상세 블록 없음).
  //   prefix로 구분: 계측기='p-', 단말기=''. 뒤로가기 스크롤 복원은 react-router navigate로 유지.
  const card = (it: Instrument, type: string, prefix: string) => {
    // [2026-10-08 사용자] 홈 카드 클릭 → 제품 '독립 상세페이지'로(기획서 v7). 구 '#p-iX'·'/safety#products' 앵커는
    //   /products·/safety 재구성으로 폐지됨 → PRODUCT_DETAIL_ROUTE(공유) 우선. 미매핑(가스 등 예정)만 유형 필터로 폴백.
    const to = PRODUCT_DETAIL_ROUTE[it.id] || (it as Instrument & { to?: string }).to || `/products?type=${type}`;
    return it.soon
      ? <article key={it.id} className={`${s.card} ${s.soon} anim-scroll`}>{cardInner(it)}</article>
      : <a key={it.id} href={to} onClick={go(to)} className={`${s.card} ${s.link} anim-scroll`} aria-label={`${it.label} — 제품 보기`}>{cardInner(it)}</a>;
  };

  return (
    <section className={s.section}>
      <div className={s.inner} ref={reveal}>
        <div className={s.head}>
          <h2 className={`${s.title} anim-scroll`}>{instrumentsSection.title}</h2>
          <div className={`${s.aside} anim-scroll`}>
            <p>{instrumentsSection.desc}</p>
            <Button variant="pill" href="/products" onClick={go('/products')}>{instrumentsSection.cta}</Button>
          </div>
        </div>

        {/* 안전 단말기(산업안전보건) — 우선순위 상단(2026-09-05 사용자). 신규 대표 도메인 강조.
            체감온도계 카드 = 'B안'(2026-09-06 확정): 중요부위 크롭(fill)로 계측기와 동일 정사각 박스. */}
        <p className={`${s.groupLabel} ${s.safe} anim-scroll`}><b>안전</b> · 산업안전보건 단말기</p>
        <div className={s.grid}>{SAFETY_TERMINALS.map((it) => card(it, 'terminal', ''))}</div>

        {/* 환경 계측기(방지시설) */}
        <p className={`${s.groupLabel} ${s.env} anim-scroll`}><b>환경</b> · 대기환경 방지시설 계측기</p>
        <div className={s.grid}>{INSTRUMENTS.map((it) => card(it, 'instrument', 'p-'))}</div>
      </div>
    </section>
  );
});

export default Instruments;
